import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Grid, Divider, Stack, Typography, Box, Card } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { getUsers } from '../../../redux/slices/users';
import Iconify from '../../../reusable-components/iconify';
import { fMonthDayTimeYear } from '../../../utils/formatTime';
import { stringToColor } from '../../../utils/cssStyles';
import Markdown from '../../../reusable-components/markdown';
import { CustomAvatar } from '../../../reusable-components/custom-avatar';
import { useTabs } from '../../../context/TabContext';
import { highlightText } from '../../../reusable-components/highlight-element/highlight-element';
import { HOST_API_URL } from '../../../config-global';
import FileThumbnail from '../../../reusable-components/file-thumbnail';

TicketsResults.propTypes = {
  searchResult: PropTypes.object,
  search: PropTypes.string,
};

export default function TicketsResults({ searchResult, search }) {
  const { addNewTabAndNavigate } = useTabs();

  const [open, setOpen] = useState(true);

  const { data: { users }} = useSelector((state) => state.users);
  const dispatch = useDispatch();

  const getTicketFile = async (noteId) => {
    window.open(`${HOST_API_URL}Api/TicketFile?noteId=${noteId}`, '_blank');
  };

  useEffect(() => {
    if(!users.length) {
      dispatch(getUsers());
    }
  }, [])

  const getUserByEmail = (email) => users.find(u => u.email === email);

  return (
    <Card variant="outlined" sx={{ border: "1.5px #e5e9ed solid", p: 2, mb: 3, borderRadius: '20px'}} key={searchResult.id}>
      <Grid
        container
        direction="row"
        justifyContent="space-between"
        alignItems="flex-end"
        onClick={() => setOpen(!open)}
        sx={{
          cursor: 'pointer',
          width: '100%',
        }}
      >
        <Grid item>
          <Box>
            <Typography variant="subtitle1">
              <Markdown convertPToDiv children={`<s1>Ticket #${highlightText(searchResult.subticket?.subTicketName ?? searchResult.id, search)} | ${highlightText(searchResult.subject, search)}</s1>`} />
            </Typography>
          </Box>
          {searchResult.facility && (
            <Box>
              <Typography variant="subtitle2">
                <Markdown convertPToDiv children={`<s2>Facility: ${highlightText(searchResult.facility, search)}</s2>`} />
              </Typography>
            </Box>
          )}
          {searchResult.createdBy && (
            <Box>
              <Typography variant="subtitle2">
                <Markdown
                  convertPToDiv
                  children={`<s2>Created By: ${getUserByEmail(searchResult.createdBy)?.firstName || ''} ${getUserByEmail(searchResult.createdBy)?.lastName || ''} ${highlightText(searchResult.createdBy, search)}</s2>`} />
              </Typography>
            </Box>
          )}
        </Grid>
        <Typography variant="body2" sx={{ fontWeight: 'bold', cursor: 'pointer' }} color="primary">
          <Iconify icon={open ? 'mdi:chevron-up' : 'mdi:chevron-down'} width={16} height={16} />
        </Typography>
      </Grid>
      {open && (
        <Box sx={{ width: '100%' }}>
          <Divider sx={{ width: '100%', my: 1, backgroundColor: '#e5e9ed' }} />
          {searchResult.messages?.map((message, index) => (
            <Grid item key={index} sx={{width: '100%'}}>
              <Stack direction="row" justifyContent={'flex-start'} sx={{ mt: 2 }}>
                <>
                  <CustomAvatar name={message.sentBy} sx={{ mr: 1 }} />
                  <Stack spacing={0.5} alignItems={'flex-start'} sx={{width: '100%'}}>
                    <>
                      <Typography noWrap variant="caption" sx={{ color: 'text.disabled' }}>
                        {fMonthDayTimeYear(message.sentOn)}
                        <Markdown convertPToDiv children={`<s2>${getUserByEmail(message.sentBy)?.firstName || ''} ${getUserByEmail(message.sentBy)?.lastName || ''} ${highlightText(message.sentBy, search)}`}/>
                      </Typography>
                    </>
                    <Box sx={{width: '100%'}}>
                      <Stack
                        sx={{
                          p: 1,
                          width: '100%',
                          borderRadius: '9px',
                          backgroundColor: '#f2f5f7',
                          wordWrap: 'break-word',
                          typography: 'body2',
                          border: 'solid 1px #e5e9ed',
                          display: 'inline-block',
                          wordBreak: 'break-word',
                          overflowWrap: 'break-word',
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: '#e5e9ed',
                            border: '1px solid #dee4e9'
                          }
                        }}
                        onClick={() => addNewTabAndNavigate(searchResult.subticket?.parentTicketId > 0
                          ? `/dashboard/tickets/${searchResult.subticket.parentTicketId}?subticket=${searchResult.id}`
                          : `/dashboard/tickets/${searchResult.id}`)}
                      >
                        <Box
                          sx={{
                            '& img': {
                              cursor: 'pointer',
                              '&:hover': {
                                filter: 'brightness(0.9)',
                                padding: 0.3,
                                border: () => `solid 1px ${stringToColor(message.sentBy)}`,
                              },
                            },
                          }}
                        >
                          {!message.file ? 
                            <Markdown convertPToDiv children={highlightText(message.message, search)} /> :
                            <Box
                              key={message.id}
                              width="100%"
                              sx={{
                                flexShrink: 0,
                                borderRadius: 1,
                                overflow: 'hidden',
                                position: 'relative',
                                display: 'flex',
                                padding: '3px',
                                mx: 0.5,
                                my: 0.1,
                                width: 'fit-content',
                              }}
                            >
                              <FileThumbnail
                                file={message.message}
                                tooltip
                                onDownload={() => getTicketFile(message.id)}
                              />
                              <Typography variant="caption" sx={{color: (theme) => theme.palette.grey[700], ml: 1}}>
                                {message.message}
                              </Typography>
                            </Box>
                          }
                        </Box>
                      </Stack>
                    </Box>
                  </Stack>
                </>
              </Stack>
            </Grid>
          ))}
        </Box>
      )}
    </Card>
  );
}
